import { useNavigate } from 'react-router-dom';
import { CheckSquare, DollarSign, FileText, Calendar, RotateCcw } from 'lucide-react';
import { Card } from '@/components/ui';
import { useItemStore, useSettingsStore, useAuthStore } from '@/stores';
import { useMemo } from 'react';
import type { TaskItem } from '@perfin/shared';

interface SummaryCardsProps {
  ownerFilter?: string | null;
}

export function SummaryCards({ ownerFilter }: SummaryCardsProps) {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const tasks = useItemStore((state) => state.getTasks());
  const transactions = useItemStore((state) => state.getTransactions());
  const notes = useItemStore((state) => state.getNotes());
  const keyDates = useItemStore((state) => state.getKeyDates());
  const { currentUserId } = useSettingsStore();
  const isAdmin = useSettingsStore((state) => state.currentUserRole === 'admin' || state.currentUserRole === 'superAdmin');

  const stats = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
    const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    const sevenDaysFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    const thirtyDaysFromNow = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);

    // Filter items by owner filter
    // If ownerFilter is set, filter by that owner
    // If ownerFilter is null/empty (All Owners): admins see all, standard users see owned/created
    const filterByOwner = <T extends { owner?: string; createdBy: string }>(items: T[]): T[] => {
      if (ownerFilter) {
        return items.filter((item) => item.owner === ownerFilter);
      }
      // All Owners selected
      if (isAdmin) {
        return items; // Admins see all
      }
      // Standard users see items they own OR created
      return items.filter((item) => item.owner === currentUserId || item.createdBy === user?.uid);
    };

    const myTasks = filterByOwner(tasks);
    const myChoreWheelTasks = myTasks.filter((t) => (t as TaskItem & { choreWheel?: boolean }).choreWheel);
    const myRegularTasks = myTasks.filter((t) => !(t as TaskItem & { choreWheel?: boolean }).choreWheel);
    const myTransactions = filterByOwner(transactions);
    const myNotes = filterByOwner(notes);
    const myKeyDates = filterByOwner(keyDates);

    // Helper to check if date falls within range
    const isWithinRange = (date: Date, start: Date, end: Date) => date >= start && date < end;
    const isWithinLast = (date: Date, daysAgo: Date) => date >= daysAgo && date < tomorrow;

    // ===== CHORE WHEEL =====
    const choreWheelTotal = myChoreWheelTasks.length;
    // Today: tasks with dueDate = today
    const choreWheelTodayTotal = myChoreWheelTasks.filter((t) => {
      if (!t.dueDate) return false;
      const due = t.dueDate.toDate();
      return isWithinRange(due, today, tomorrow);
    }).length;
    const choreWheelTodayComplete = myChoreWheelTasks.filter((t) => {
      if (!t.dueDate) return false;
      const due = t.dueDate.toDate();
      return isWithinRange(due, today, tomorrow) && t.status === 'complete';
    }).length;
    // Last 7d: tasks with dueDate in last 7 days
    const choreWheel7dTotal = myChoreWheelTasks.filter((t) => {
      if (!t.dueDate) return false;
      const due = t.dueDate.toDate();
      return isWithinLast(due, sevenDaysAgo);
    }).length;
    const choreWheel7dComplete = myChoreWheelTasks.filter((t) => {
      if (!t.dueDate) return false;
      const due = t.dueDate.toDate();
      return isWithinLast(due, sevenDaysAgo) && t.status === 'complete';
    }).length;

    // ===== TASKS =====
    const tasksTotal = myRegularTasks.length;
    const tasksTodayTotal = myRegularTasks.filter((t) => {
      if (!t.dueDate) return false;
      const due = t.dueDate.toDate();
      return isWithinRange(due, today, tomorrow);
    }).length;
    const tasksTodayComplete = myRegularTasks.filter((t) => {
      if (!t.dueDate) return false;
      const due = t.dueDate.toDate();
      return isWithinRange(due, today, tomorrow) && t.status === 'complete';
    }).length;
    const tasks7dTotal = myRegularTasks.filter((t) => {
      if (!t.dueDate) return false;
      const due = t.dueDate.toDate();
      return isWithinLast(due, sevenDaysAgo);
    }).length;
    const tasks7dComplete = myRegularTasks.filter((t) => {
      if (!t.dueDate) return false;
      const due = t.dueDate.toDate();
      return isWithinLast(due, sevenDaysAgo) && t.status === 'complete';
    }).length;

    // ===== KEY DATES =====
    const keyDatesTotal = myKeyDates.length;
    const keyDatesNext7d = myKeyDates.filter((d) => {
      if (!d.dueDate) return false;
      const due = d.dueDate.toDate();
      return isWithinRange(due, today, sevenDaysFromNow);
    }).length;
    const keyDatesNext30d = myKeyDates.filter((d) => {
      if (!d.dueDate) return false;
      const due = d.dueDate.toDate();
      return isWithinRange(due, today, thirtyDaysFromNow);
    }).length;

    // ===== TRANSACTIONS (draft = pending) =====
    const transactionsDraftTotal = myTransactions.filter((t) => t.status === 'draft').length;
    const transactions7dTotal = myTransactions.filter((t) => {
      const created = t.createdAt.toDate();
      return isWithinLast(created, sevenDaysAgo);
    }).length;
    const transactions7dDraft = myTransactions.filter((t) => {
      const created = t.createdAt.toDate();
      return isWithinLast(created, sevenDaysAgo) && t.status === 'draft';
    }).length;
    const transactions30dTotal = myTransactions.filter((t) => {
      const created = t.createdAt.toDate();
      return isWithinLast(created, thirtyDaysAgo);
    }).length;
    const transactions30dDraft = myTransactions.filter((t) => {
      const created = t.createdAt.toDate();
      return isWithinLast(created, thirtyDaysAgo) && t.status === 'draft';
    }).length;

    // ===== NOTES (draft = pending) =====
    const notesTotal = myNotes.length;
    const notes7dTotal = myNotes.filter((n) => {
      const created = n.createdAt.toDate();
      return isWithinLast(created, sevenDaysAgo);
    }).length;
    const notes7dDraft = myNotes.filter((n) => {
      const created = n.createdAt.toDate();
      return isWithinLast(created, sevenDaysAgo) && (n as unknown as { status?: string }).status === 'draft';
    }).length;
    const notes30dTotal = myNotes.filter((n) => {
      const created = n.createdAt.toDate();
      return isWithinLast(created, thirtyDaysAgo);
    }).length;
    const notes30dDraft = myNotes.filter((n) => {
      const created = n.createdAt.toDate();
      return isWithinLast(created, thirtyDaysAgo) && (n as unknown as { status?: string }).status === 'draft';
    }).length;

    return {
      choreWheel: { total: choreWheelTotal, todayComplete: choreWheelTodayComplete, todayTotal: choreWheelTodayTotal, week7dComplete: choreWheel7dComplete, week7dTotal: choreWheel7dTotal },
      tasks: { total: tasksTotal, todayComplete: tasksTodayComplete, todayTotal: tasksTodayTotal, week7dComplete: tasks7dComplete, week7dTotal: tasks7dTotal },
      keyDates: { total: keyDatesTotal, next7d: keyDatesNext7d, next30d: keyDatesNext30d },
      transactions: { draftTotal: transactionsDraftTotal, week7dDraft: transactions7dDraft, week7dTotal: transactions7dTotal, month30dDraft: transactions30dDraft, month30dTotal: transactions30dTotal },
      notes: { total: notesTotal, week7dDraft: notes7dDraft, week7dTotal: notes7dTotal, month30dDraft: notes30dDraft, month30dTotal: notes30dTotal },
    };
  }, [tasks, transactions, notes, keyDates, currentUserId, ownerFilter, isAdmin, user?.uid]);

  const handleCardClick = (href: string) => {
    // Just navigate - each dashboard has its own independent owner filter in localStorage
    navigate(href);
  };

  const cards = [
    {
      title: 'My Chore Wheel',
      value: `${stats.choreWheel.total} total`,
      line1: `Today: ${stats.choreWheel.todayComplete}/${stats.choreWheel.todayTotal} complete`,
      line2: `Last 7d: ${stats.choreWheel.week7dComplete}/${stats.choreWheel.week7dTotal} complete`,
      icon: RotateCcw,
      color: 'text-orange-600 bg-orange-100',
      href: '/chore-wheel',
    },
    {
      title: 'My Tasks',
      value: `${stats.tasks.total} total`,
      line1: `Today: ${stats.tasks.todayComplete}/${stats.tasks.todayTotal} complete`,
      line2: `Last 7d: ${stats.tasks.week7dComplete}/${stats.tasks.week7dTotal} complete`,
      icon: CheckSquare,
      color: 'text-blue-600 bg-blue-100',
      href: '/tasks',
    },
    {
      title: 'My Key Dates',
      value: `${stats.keyDates.total} total`,
      line1: `Next 7d: ${stats.keyDates.next7d} items`,
      line2: `Next 30d: ${stats.keyDates.next30d} items`,
      icon: Calendar,
      color: 'text-purple-600 bg-purple-100',
      href: '/dates',
    },
    {
      title: 'My Transactions',
      value: `${stats.transactions.draftTotal} draft`,
      line1: `Last 7d: ${stats.transactions.week7dDraft}/${stats.transactions.week7dTotal} draft`,
      line2: `Last 30d: ${stats.transactions.month30dDraft}/${stats.transactions.month30dTotal} draft`,
      icon: DollarSign,
      color: 'text-green-600 bg-green-100',
      href: '/transactions',
    },
    {
      title: 'My Notes',
      value: `${stats.notes.total} total`,
      line1: `Last 7d: ${stats.notes.week7dDraft}/${stats.notes.week7dTotal} draft`,
      line2: `Last 30d: ${stats.notes.month30dDraft}/${stats.notes.month30dTotal} draft`,
      icon: FileText,
      color: 'text-yellow-600 bg-yellow-100',
      href: '/notes',
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <Card
            key={card.title}
            hover
            className="h-full cursor-pointer"
            onClick={() => handleCardClick(card.href)}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-gray-500 break-words">{card.title}</p>
                <p className="text-lg font-bold text-gray-900 mt-1">{card.value}</p>
                <p className="text-xs text-gray-400 mt-1">{card.line1}</p>
                <p className="text-xs text-gray-400">{card.line2}</p>
              </div>
              <div className={`p-2 rounded-lg flex-shrink-0 ${card.color}`}>
                <Icon className="h-4 w-4" />
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
